import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabaseClient'
import CouponCard, { CouponCardData } from '../../components/coupons/CouponCard'
import SendCouponEmailModal from '../../components/coupons/SendCouponEmailModal'
import '../../styles/coupon-print.css'

// ── Types ──────────────────────────────────────────────

type CouponRow = {
  id: string
  code: string
  type: 'USER' | 'PARTNER'
  partner_id: string | null
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  user_discount_pct: number | null
  max_usage_count: number | null
  current_usage_count: number
  is_active: boolean
  partner?: { name: string } | null
}

// ── Styles ─────────────────────────────────────────────

const styles = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
  } as React.CSSProperties,
  btnPrimary: {
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSecondary: {
    padding: '0.5rem 1rem',
    background: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  btnSuccess: {
    padding: '0.5rem 1rem',
    background: '#22c55e',
    color: 'white',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  toast: (type: 'success' | 'error'): React.CSSProperties => ({
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    padding: '0.75rem 1.25rem',
    borderRadius: '0.5rem',
    color: 'white',
    fontWeight: 500,
    fontSize: '0.9rem',
    zIndex: 2000,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    background: type === 'success' ? '#22c55e' : '#dc2626',
  }),
}

// ── Component ──────────────────────────────────────────

const CouponViewerPage: React.FC = () => {
  const { couponId } = useParams<{ couponId: string }>()
  const navigate = useNavigate()

  const [coupon, setCoupon] = useState<CouponRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  // ── Load coupon ──
  useEffect(() => {
    if (!couponId) return

    const load = async () => {
      setLoading(true)
      setError(null)

      const { data, error: err } = await supabase
        .from('coupons')
        .select('*, partner:partners(name)')
        .eq('id', couponId)
        .single()

      if (err || !data) {
        setError(err?.message ?? 'Coupon introuvable.')
      } else {
        setCoupon(data as CouponRow)
      }
      setLoading(false)
    }
    load()
  }, [couponId])

  // ── Auto-dismiss toast ──
  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(timer)
  }, [toast])

  // ── Derived data ──
  const cardData: CouponCardData | null = coupon
    ? {
        code: coupon.code,
        type: coupon.type,
        discount_pct:
          coupon.type === 'USER'
            ? coupon.user_discount_pct ?? 0
            : coupon.member_discount_pct,
        valid_from: coupon.valid_from,
        valid_until: coupon.valid_until,
        partner_name: coupon.partner?.name ?? null,
        is_active: coupon.is_active,
      }
    : null

  // ── Handlers ──
  const handlePrint = () => {
    window.print()
  }

  const handleEmailSuccess = () => {
    setToast({ message: 'Coupon envoyé avec succès ✉️', type: 'success' })
  }

  const handleEmailError = (msg: string) => {
    setToast({ message: msg, type: 'error' })
  }

  // ── Render ──
  if (loading) {
    return (
      <div style={styles.container}>
        <p style={{ color: '#64748b' }}>Chargement du coupon…</p>
      </div>
    )
  }

  if (error || !coupon || !cardData) {
    return (
      <div style={styles.container}>
        <p style={{ color: '#dc2626' }}>{error ?? 'Coupon introuvable.'}</p>
        <button style={styles.btnSecondary} onClick={() => navigate('/admin/coupons')}>
          ← Retour aux coupons
        </button>
      </div>
    )
  }

  return (
    <div style={styles.container} data-testid="coupon-viewer-page">
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', color: '#1e293b', margin: 0 }}>
          🎟️ Aperçu du coupon
        </h1>
        <button style={styles.btnSecondary} onClick={() => navigate('/admin/coupons')}>
          ← Retour
        </button>
      </div>

      {/* Coupon Card */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '2rem',
        }}
      >
        <CouponCard coupon={cardData} />
      </div>

      {/* Actions */}
      <div
        className="coupon-actions"
        style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
        }}
      >
        <button
          style={styles.btnPrimary}
          onClick={handlePrint}
          data-testid="print-coupon-btn"
        >
          🖨️ Imprimer le coupon
        </button>
        <button
          style={styles.btnSuccess}
          onClick={() => setShowEmailModal(true)}
          data-testid="send-email-coupon-btn"
        >
          📧 Envoyer par email
        </button>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <SendCouponEmailModal
          couponId={coupon.id}
          couponCode={coupon.code}
          onClose={() => setShowEmailModal(false)}
          onSuccess={handleEmailSuccess}
          onError={handleEmailError}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={styles.toast(toast.type)} data-testid="coupon-toast">
          {toast.message}
        </div>
      )}
    </div>
  )
}

export default CouponViewerPage
